import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

import { map } from 'rxjs/operators';

import { Gtp } from '../../shared/gtp';
import { environment } from 'src/environments/environment';
import { Constant } from '../../shared/constant';

import { Subject } from 'rxjs';

/**
 * Servicio para autenticación de Usuario
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  public accessToken: string;
  public headers;
  user: any = {};
  private loginStatus = new Subject<any>();
  
  // 🔒 CONTROL DE BÚSQUEDAS: Límite por sesión para reducir costos
  private readonly LIMITE_BUSQUEDAS_POR_SESION = 5;
  private contadorBusquedas: number = 0;
  private busquedasHabilitadas: boolean = true;

  constructor(
    public gtp : Gtp,
    public constant : Constant,
    public router: Router,
    public http: HttpClient,
  ) { 
    // Inicializar contador desde localStorage si existe
    this.cargarContadorBusquedas();
  }

  /**
   * Verificar los datos de login del usuario en el API
   * @param model Recibe los parámetros de usuario y password como objecto
   * @example
   * login({ username: 'usuario', password: 'password' })
   */
  login(model: any) {
    let data = {
      name: model.username,
      clave: model.password,
      accesoweb: true,
      observe: 'response'
    }
    let options = {
      headers: new HttpHeaders({
        'Content-Type' : 'application/json',
        'Cache-Control': 'no-cache'
      })
    };
    return this.http.post(`${environment.baseUrl}auth/login`, data, options)
    .pipe(map((response: Response) => {
        // login exitoso.
        this.user = response;
        if (this.user && this.user.token) {
            this.setHeaders(this.user.token);
            //se guarda en sesion la respuesta del servicio.

            // 1 = Plan Básico
            // 2 = Plan Full
            let cliente_plan_id = (this.user.cliente_plan_id) ? this.user.cliente_plan_id : 2;
            //Limite de días a consultar            
            let limiteDiasPlanBasico = 3;
            let limiteDiasPlanFull = 31;
            //cliente_plan_id = 1; // para debug, comentar para producción

            let currentUser = {
              username: model.username,
              token: this.user.token,
              cod_cliente: this.user.cod_cliente,
              perfil: this.user.perfil,
              cliente_plan_id: cliente_plan_id,
              serverPath: this.user.api,              
              baseUrl: ``,
              baseUrlV2: ``,
              limiteconsultas: (cliente_plan_id == 1) ? limiteDiasPlanBasico : limiteDiasPlanFull,
            };
            
            currentUser.baseUrl = `${environment.host}${currentUser.serverPath}/${environment.version}`;
            currentUser.baseUrlV2 = `${environment.host}${currentUser.serverPath}/${environment.version2}`;

            this.gtp.setItemLocalStorage('currentUser', currentUser);
            this.loginStatus.next(this.user);
        }
        return this.user;
      })
    );
  }

  /**
   * Seteo de headers
   * @param accessToken String del otken
   */
  setHeaders(accessToken: string) {
    this.accessToken = accessToken;
    let httpHeaders = new HttpHeaders({
      'Content-Type' : 'application/json',
      'Cache-Control': 'no-cache',
      'Authorization': `Bearer ${this.accessToken}`
    });
    this.headers = {
      headers: httpHeaders
    };
  }

  /**
   * Desloguea al usuario removiendo el localstorage y re enviando a login
   */
  logout(): void {
    this.setloginStatus(false);
    this.gtp.setMainOutlet(false);
    this.gtp.removeItemLocalStorage('currentUser');
    
    // 🔒 Resetear contador de búsquedas al cerrar sesión
    this.resetearContadorBusquedas();
    
    this.router.navigate(['login']);
    //this.gtp.setMainRoute('/login');
  }

  /**
   * Consulta si está autenticado
   */
  isAuthenticated() {
    return this.loginStatus.asObservable();
    //return this.gtp.getItemLocalStorage('currentUser') != null;
  }

  /**
   * Consulta si inicialmente está autenticado
   */
  isInitialAuthenticated() {
    return this.gtp.getItemLocalStorage('currentUser') != null;
  }

  setloginStatus(obj){
    this.loginStatus.next(obj);
  }

  // ==================== 🔒 CONTROL DE BÚSQUEDAS POR SESIÓN ====================

  /**
   * Carga el contador de búsquedas desde localStorage
   */
  private cargarContadorBusquedas(): void {
    const contador = localStorage.getItem('busquedas_sesion');
    if (contador) {
      this.contadorBusquedas = parseInt(contador, 10);
      this.busquedasHabilitadas = this.contadorBusquedas < this.LIMITE_BUSQUEDAS_POR_SESION;
      console.log(`📊 Contador de búsquedas cargado: ${this.contadorBusquedas}/${this.LIMITE_BUSQUEDAS_POR_SESION}`);
    }
  }

  /**
   * Verifica si se puede hacer una búsqueda
   * @returns true si puede buscar, false si alcanzó el límite
   */
  puedeBuscar(): boolean {
    const puede = this.contadorBusquedas < this.LIMITE_BUSQUEDAS_POR_SESION;
    
    if (!puede) {
      console.warn(`🚫 Límite de búsquedas alcanzado: ${this.contadorBusquedas}/${this.LIMITE_BUSQUEDAS_POR_SESION}`);
    }
    
    return puede;
  }

  /**
   * Incrementa el contador de búsquedas
   */
  incrementarBusqueda(): void {
    this.contadorBusquedas++;
    localStorage.setItem('busquedas_sesion', this.contadorBusquedas.toString());
    
    console.log(`🔍 Búsqueda registrada: ${this.contadorBusquedas}/${this.LIMITE_BUSQUEDAS_POR_SESION}`);
    
    // Deshabilitar búsquedas si alcanzó el límite
    if (this.contadorBusquedas >= this.LIMITE_BUSQUEDAS_POR_SESION) {
      this.busquedasHabilitadas = false;
      console.warn(`🚫 Límite de búsquedas alcanzado. No se permiten más búsquedas en esta sesión.`);
    }
  }

  /**
   * Obtiene búsquedas restantes
   */
  getBusquedasRestantes(): number {
    return Math.max(0, this.LIMITE_BUSQUEDAS_POR_SESION - this.contadorBusquedas);
  }

  /**
   * Resetea el contador de búsquedas (al cerrar sesión)
   */
  resetearContadorBusquedas(): void {
    this.contadorBusquedas = 0;
    this.busquedasHabilitadas = true;
    localStorage.removeItem('busquedas_sesion');
    console.log('🔄 Contador de búsquedas reseteado');
  }

  /**
   * Obtiene el límite de búsquedas
   */
  getLimiteBusquedas(): number {
    return this.LIMITE_BUSQUEDAS_POR_SESION;
  }

  /**
   * Obtiene el contador actual
   */
  getContadorBusquedas(): number {
    return this.contadorBusquedas;
  }

}
